import Vue from 'vue';
import { injectable } from 'inversify-props';
import dayjs from 'dayjs';
import BaseService from '@/services/base-service';
import ProcessFlowModel from '@/models/crm/process-flow.model';
import ProcessKanbanSettingsModel from '@/models/crm/process-kanban-settings.model';
import ProcessModel from '@/models/crm/process.model';
import SettingsModel from '@/models/crm/settings.model';
import { PaginationModel } from '@/models/pagination.model';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import { ProcessFlowTypeEnum } from '@/enums/crm/process-flow-type.enum';
import { RequestConfig } from '@/interfaces/request-config.interface';
import { i18n } from '@/i18n-setup';

type ProcessRouteFlag = 'flagUtilizaKanbanReact' | 'flagAbrirProcessoNovaGuiaPainelRelacionamento';

export type ProcessRouteSettings = Partial<Pick<SettingsModel, ProcessRouteFlag>>;

export interface IOpenInNewProcessRouteOptions {
  processId: number | undefined;
  tipoFluxo: number | undefined;
  flowName?: string;
  settings?: ProcessRouteSettings | null;
}

interface IOpenProcessOptions {
  flowName?: string;
  opensInBrowserTab: boolean;
}

@injectable()
export default class ProcessService extends BaseService {
  async getProcesses(
    clientId: string,
    type: ClientTypeEnum,
    limit = 0,
    returnActivesOnly = true,
  ): Promise<ProcessModel[]> {
    const config: RequestConfig = {
      params: {
        clientId,
        limit,
        type,
        returnActivesOnly,
      },
    };

    return (await this.httpService.get(
      ProcessModel,
      `${this.getApiPath()}/crm/processo/list`,
      config,
    )) as ProcessModel[];
  }

  async getAttendantProcesses(offset: number): Promise<PaginationModel<ProcessModel>> {
    const config: RequestConfig = {
      params: {
        offset,
      },
    };
    return (await this.httpService.get(
      PaginationModel,
      `${this.getApiPath()}/attendant/processes`,
      config,
    )) as PaginationModel<ProcessModel>;
  }

  async getFlows(): Promise<ProcessFlowModel[]> {
    const flows = (await this.httpService.post(
      ProcessFlowModel,
      `${this.getApiPath()}/processos/fluxosUsuario?token=${this.getApiToken()}`,
      {},
    )) as ProcessFlowModel[];

    // A API devolve na ordem em que varre os fluxos do usuário. Como a lista é de escolha,
    // ordena por nome. `numeric` cobre quem prefixa o nome com número (convenção de alguns
    // clientes, não do produto): sem ele, "9 - X" cairia depois de "10 - X".
    return flows.sort((a, b) => (a.nome ?? '')
      .localeCompare(b.nome ?? '', 'pt-BR', { numeric: true, sensitivity: 'base' }));
  }

  async getKanbanSettings(): Promise<ProcessKanbanSettingsModel> {
    return (await this.httpService.get(
      ProcessKanbanSettingsModel,
      `${this.getApiPath()}/crm/processo/kanban-settings`,
      {},
    )) as ProcessKanbanSettingsModel;
  }

  getKanbanUrl(
    urlKanbanApp: string,
    urlKanbanApi: string,
    flowId: number,
    clientId: string,
    clientType: ClientTypeEnum,
    defaultQueue: number,
    processId?: number,
  ): string {
    let kanbanUrl = `${urlKanbanApp}?token=${encodeURIComponent(this.getApiToken())}`;
    kanbanUrl += `&origin=crm360&urlIgs=${urlKanbanApi}&fluxo=${flowId}`;

    if (clientType === ClientTypeEnum.Prospect) {
      kanbanUrl += `&defaultProspect=${clientId}`;
    } else {
      kanbanUrl += `&defaultClient=${clientId}`;
    }

    if (processId) {
      kanbanUrl += `&onLoadOpenProcess=${processId}`;
    } else {
      kanbanUrl += '&onLoadCreateProcess=true';
    }

    if (defaultQueue && defaultQueue > 0) {
      kanbanUrl += `&defaultQueue=${defaultQueue}`;
    }

    return kanbanUrl;
  }

  getNewKanbanUrl(
    flowId: number,
    clientId: string,
    clientType: ClientTypeEnum,
    defaultQueue: number,
    processId?: number,
    isProcessView?: boolean,
    clienteRep?: string,
    fromPainelRelacionamento?: boolean,
  ): string {
    let kanbanUrl = `${this.getApi360Path()}`;
    if (isProcessView) {
      kanbanUrl = `${kanbanUrl}/crm/processo`
      + `/${processId ? `${processId}` : '0'}`
      + `${flowId ? `!idFluxo=${flowId}` : ''}`;
    } else {
      kanbanUrl = `${kanbanUrl}/crm/workflow/kanban/${flowId}`
      + `${processId ? `!${processId}` : ''}`;
    }
    if (!processId) {
      kanbanUrl = `${kanbanUrl}`
      + `${clientType ? `!tipoCliente=${ClientTypeEnum.Client.toString() === clientType
        ? 'CLIENTE' : clientType.toUpperCase()}` : ''}`
      + `${clientId ? `!idCliente=${clientId}` : ''}`
      + `${defaultQueue && defaultQueue !== -1 ? `!filaPadrao=${defaultQueue}` : ''}`;
    }
    if (clienteRep) {
      kanbanUrl = `${kanbanUrl}!clienteRep=${clienteRep}`;
    }
    if (fromPainelRelacionamento) {
      kanbanUrl = `${kanbanUrl}!fromPainelRelacionamento=true`;
    }
    kanbanUrl = `${kanbanUrl}!token=${encodeURIComponent(this.getApiToken())}`;
    kanbanUrl = `${kanbanUrl}!u=${this.getApiPath()}`;
    kanbanUrl = kanbanUrl.replace(/!/, '?');
    kanbanUrl = kanbanUrl.replaceAll('!', '&');
    return kanbanUrl;
  }

  static isSalesFunnelFlow(tipoFluxo?: number): boolean {
    return tipoFluxo === ProcessFlowTypeEnum.SalesFunnel;
  }

  // Com o Kanban React ligado todo processo abre na rota única do React; desligado, só o funil
  // (que já era do React) — os demais seguem no Kanban do V6.
  // Em builtInMode (B2SP, host V24) a rota nova abre sem token e o React pede login: só o funil,
  // que já tinha essa limitação, vai para ela; os demais mantêm a URL do V6 com token.
  static usesNewProcessRoute(
    tipoFluxo: number | undefined,
    settings?: ProcessRouteSettings | null,
    builtInMode = false,
  ): boolean {
    if (ProcessService.isSalesFunnelFlow(tipoFluxo)) {
      return true;
    }
    return !!settings?.flagUtilizaKanbanReact && !builtInMode;
  }

  // Sem `t`: o token do Vue vem de outro tokenizer e o React do Geo360 recusa.
  getProcessUrl(processId: number): string {
    return `${this.getApi360Path()}/react/crm/processes/${processId}`;
  }

  // Devolve false quando o processo segue o caminho legado da tela (aba V6, dialog ou URL do Kanban).
  // Falha ao abrir conta como tratada: já foi avisada aqui, e o caminho legado não deve abrir outra coisa.
  openInNewProcessRoute({
    processId, tipoFluxo, flowName, settings,
  }: IOpenInNewProcessRouteOptions): boolean {
    if (!processId || !ProcessService.usesNewProcessRoute(tipoFluxo, settings, this.sessionService.builtInMode)) {
      return false;
    }

    try {
      this.openProcess(processId, {
        flowName,
        opensInBrowserTab: !!settings?.flagAbrirProcessoNovaGuiaPainelRelacionamento,
      });
    } catch (error) {
      console.error('Erro ao abrir o processo na rota nova:', error);
      Vue.prototype.$notify?.error(i18n.t('crm.process.openError').toString());
    }
    return true;
  }

  // O rotulo carrega o fluxo porque o processo abre em aba propria: com varios
  // abertos, so o numero nao distingue um do outro. Mesmo formato do card do
  // Kanban (CRMPRKanbanProcessoCard), para a aba nao mudar conforme a origem.
  private openProcess(processId: number, { flowName, opensInBrowserTab }: IOpenProcessOptions): void {
    const title = ProcessService.processTabTitle(processId, flowName);
    if (!opensInBrowserTab && this.openGeoTab(`crm/processes/${processId}`, title)) {
      return;
    }

    window.open(this.getProcessUrl(processId), '_blank');
  }

  private static processTabTitle(processId: number, flowName?: string): string {
    return flowName
      ? i18n.t('crm.salesFunnel.tabTitle', { id: processId, flow: flowName }).toString()
      : i18n.t('crm.process.tabTitle', { id: processId }).toString();
  }

  openProcessInGeoTab(processId: number, flowId: number, flowName?: string): boolean {
    const route = `crm/processo/${processId}?idFluxo=${flowId}&fromPainelRelacionamento=true`;

    return this.openGeoTab(route, ProcessService.processTabTitle(processId, flowName), 'vaadin');
  }

  // Fluxo do tipo Funil de Vendas cai na tela de nova negociação — o desvio é do próprio V24,
  // no beforeEnter da rota, para valer também para o link do representante no B2SP.
  openNewProcessInGeoTab(flowId: number, flowName: string, clientId: string,
    clientType: ClientTypeEnum, defaultQueue = -1): boolean {
    // Sem fluxo ou cliente a rota nasce inválida; devolve false para o chamador cair no fallback.
    if (!flowId || !clientId || !clientType) {
      return false;
    }

    const route = `crm/processo/0?idFluxo=${flowId}`
      + `&tipoCliente=${ClientTypeEnum.Client === clientType ? 'CLIENTE' : clientType.toUpperCase()}`
      + `&idCliente=${clientId}`
      + `&filaPadrao=${defaultQueue}`;
    const title = i18n.t('crm.process.newTabTitle', { flow: flowName }).toString();

    return this.openGeoTab(route, title, 'vaadin');
  }

  // Site = host sem o primeiro rótulo: servicos-appel.geovendas.app e appel.geovendas.app são o
  // mesmo site (cookie do Geo360 vai no iframe); servicos-x.geovendas.com.br e x.geovendas.app
  // não são. Sem URL do Geo360 não há como afirmar, e o chamador mantém o comportamento atual.
  static isGeo360OnOtherSite(pageHostname: string, url360?: string): boolean {
    if (!pageHostname || !url360) {
      return false;
    }

    let host360: string;
    try {
      host360 = new URL(url360).hostname;
    } catch (e) {
      return false;
    }

    const site = (hostname: string) => hostname.toLowerCase().split('.').slice(1).join('.');
    return site(pageHostname) !== site(host360);
  }

  // Painel do Atendente: o processo nasce sem cliente — quem escolhe é o próprio Geo360.
  openNewFlowProcessInGeoTab(flowId: number, flowName: string): boolean {
    if (!flowId) {
      return false;
    }

    const route = `crm/processo/0?idFluxo=${flowId}`;
    const title = i18n.t('crm.process.newTabTitle', { flow: flowName }).toString();

    return this.openGeoTab(route, title, 'vaadin');
  }

  // Contrato do IframeTabOpener do v6 (mesma mensagem do card do Kanban): o shell gera o token
  // e abre a aba do MDI. `app` decide o Content: rota React é servida sob /react/, Vaadin não.
  // Estar em iframe não basta: em builtInMode o pai é outro host (V24, B2SP) sem listener de
  // openTab — o postMessage morreria em silêncio, então devolve false para cair no fallback.
  private openGeoTab(route: string, title: string, app: 'react' | 'vaadin' = 'react'): boolean {
    if (window.parent === window || this.sessionService.builtInMode) {
      return false;
    }

    window.parent.postMessage({
      action: 'openTab', route, title, app,
    }, '*');
    return true;
  }

  static generateProcessExportFilename(date: Date): string {
    return `Processos_${dayjs(date).format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
  }
}
